import { useCallback } from "react";
import * as XLSX from "xlsx";

export const useExcelExport = () => {
  const exportToExcel = useCallback(
    (data, columns, filename = "table-export") => {
      try {
        // Filter out non-data columns (spacer, actions, etc.)
        const dataColumns = columns.filter(
          (col) =>
            col.type !== "spacer" &&
            col.id !== "actions" &&
            col.id !== "expander"
        );

        // Create headers
        const headers = dataColumns.map((col) => {
          let headerText = col.header || col.Header;

          if (typeof headerText === "function") {
            // For function headers, try to get a meaningful fallback
            if (col.exportHeader) {
              // Use explicit export header if provided
              headerText = col.exportHeader;
            } else if (col.id) {
              // Generate from column ID
              headerText = col.id
                .replace(/([A-Z])/g, " $1")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())
                .trim();
            } else if (col.accessor) {
              // Generate from accessor
              headerText = col.accessor
                .replace(/([A-Z])/g, " $1")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())
                .trim();
            } else {
              // Try to call the function with minimal props to get a string
              try {
                const mockProps = {
                  row: {
                    original: data[0] || {},
                    id: 0,
                  },
                };
                const result = headerText(mockProps);
                // Extract meaningful text from the result if possible
                if (typeof result === "string") {
                  headerText = result;
                } else {
                  headerText = "Dynamic Column";
                }
              } catch (error) {
                headerText = "Dynamic Column";
              }
            }
          }

          return headerText;
        });

        // Process data rows
        const processedData = data.map((row, rowIndex) => {
          const processedRow = {};

          dataColumns.forEach((col) => {
            const accessor = col.accessor || col.id;
            let value = row[accessor];
            let headerKey = col.header || col.Header;

            // Handle function headers - use the generated header text
            if (typeof headerKey === "function") {
              if (col.exportHeader) {
                headerKey = col.exportHeader;
              } else if (col.id) {
                headerKey = col.id
                  .replace(/([A-Z])/g, " $1")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())
                  .trim();
              } else if (col.accessor) {
                headerKey = col.accessor
                  .replace(/([A-Z])/g, " $1")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())
                  .trim();
              } else {
                headerKey = "Dynamic Column";
              }
            }

            // Handle different data types
            if (col.type === "dropdown" && value?.value) {
              value = value.value;
            } else if (col.type === "number") {
              value = parseFloat(value) || 0;
            } else if (col.type === "custom") {
              // For custom columns like carbon output, extract the actual value
              value = value || 0;
            }

            processedRow[headerKey] = value;
          });

          return processedRow;
        });

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(processedData);

        // Set column widths
        const colWidths = dataColumns.map((col) => ({
          wch: col.width ? parseInt(col.width.replace("px", "")) / 8 : 15,
        }));
        worksheet["!cols"] = colWidths;

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        // Create blob and download
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return blob; // Return blob for further processing
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        throw error;
      }
    },
    []
  );

  const generateEFRAGTemplate = useCallback(
    (data, columns, templateType = "VSME") => {
      // Create EFRAG-specific Excel template
      const efragColumns = columns.filter(
        (col) =>
          col.type !== "spacer" && col.id !== "actions" && col.id !== "expander"
      );

      const efragData = data.map((row) => {
        const efragRow = {};

        efragColumns.forEach((col) => {
          const accessor = col.accessor || col.id;
          let value = row[accessor];

          // Handle EFRAG-specific formatting
          if (col.type === "dropdown" && value?.value) {
            value = value.value;
          } else if (col.type === "number") {
            value = parseFloat(value) || 0;
          }

          // Map to EFRAG taxonomy if needed
          const efragField = mapToEFRAGTaxonomy(accessor, col.type);
          efragRow[efragField] = value;
        });

        return efragRow;
      });

      // Create EFRAG-compliant worksheet
      const worksheet = XLSX.utils.json_to_sheet(efragData);

      // Add EFRAG metadata
      const efragMetadata = {
        Template: templateType,
        Version: "1.0",
        Generated: new Date().toISOString(),
        Standard: "EFRAG VSME",
      };

      // Insert metadata at the top
      XLSX.utils.sheet_add_json(worksheet, [efragMetadata], { origin: "A1" });
      XLSX.utils.sheet_add_json(worksheet, efragData, { origin: "A6" });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EFRAG_Data");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      return new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    },
    []
  );

  return {
    exportToExcel,
    generateEFRAGTemplate,
  };
};

// Helper function to map columns to EFRAG taxonomy
const mapToEFRAGTaxonomy = (accessor, type) => {
  const mappings = {
    invoice_date: "InvoiceDate",
    invoice_number: "InvoiceNumber",
    nickname: "Description",
    "type_material.value": "MaterialType",
    qty: "Quantity",
    weight: "Weight",
    price_invoice: "Amount",
    carbon: "CarbonEmissions",
  };

  return mappings[accessor] || accessor;
};
